import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import { Button } from 'element-react'
import { withRouter } from 'react-router-dom'
import FormMessage from '../../../components/user/includes/FormMessage'
import { LabeledInputField } from '../../../helpers/fieldHelper'
import CustomLoader from '../../../components/user/includes/CustomLoader'
import {
    tagHotjarRecording,
    createValidator,
    Status,
} from '../../../helpers/util'
import { MainSectionDefault } from '../../../components/views/includes/Wrappers'
import Spacer from '../../../components/views/includes/Spacer'
import ErrorAlert from '../../../components/views/includes/ErrorAlert'
import { logErrorOnServer } from '../../../helpers/serverLogging'
import useAuth from '../../../hooks/useAuth'
import AppRoutes from '../../../routes/appRoutes'

const validate = createValidator(['firstName', 'lastName', 'email'])

const logError = logErrorOnServer('Invite User')

const InviteUserForm = ({ companyName, impersonatedBy, sapId }) => {
    const [status, setStatus] = useState(Status.INIT)
    const [errorMessage, setErrorMessage] = useState('')
    const { addNewUser } = useAuth()

    const onUserInvitedSuccess = () => {
        setStatus(Status.SUCCESS)
    }

    const onUserInvitedError = error => {
        tagHotjarRecording('API Error')
        const {
            errors,
            message = 'Please check your internet connection and try again.',
        } = error?.response?.data ?? {}
        const errorMessage =
            errors && Array.isArray(errors.email) && errors.email[0].length > 0
                ? errors.email[0]
                : errors &&
                  Array.isArray(errors.firstName) &&
                  errors.firstName[0].length > 0
                ? errors.firstName[0]
                : errors &&
                  Array.isArray(errors.lastName) &&
                  errors.lastName[0].length > 0
                ? errors.lastName[0]
                : message

        setErrorMessage(errorMessage)
        setStatus(Status.ERROR)
        logError(errorMessage)
    }

    const onSubmit = formValues => {
        setStatus(Status.LOADING)

        // This route (ajax/register) is built into Laravel and triggers this function:
        // App/Http/Controllers/Auth/RegsiterController->register(). (95% sure on that)
        addNewUser(
            {
                sapId,
                ...formValues,
            },
            onUserInvitedSuccess,
            onUserInvitedError,
        )
    }

    return status === Status.SUCCESS ? (
        <div>
            <FormMessage
                messageTitle="User Invite Sent!"
                messageContent="Your invite user form has been sent."
                buttonLink={AppRoutes.users()}
                buttonLabel="Return to Users"
            />
        </div>
    ) : (
        <div>
            {status === Status.ERROR && <ErrorAlert error={errorMessage} />}
            {status === Status.LOADING && (
                <MainSectionDefault>
                    <Spacer size="large" />
                    <CustomLoader />
                </MainSectionDefault>
            )}
            <Form validate={validate} onSubmit={onSubmit}>
                {({ handleSubmit, pristine, submitting }) => (
                    <form id="inviteUserForm" onSubmit={handleSubmit}>
                        <label>Company</label>
                        <div className="text-container">
                            <div className="margin-bottom-small text-small font-700">
                                {companyName}
                            </div>
                        </div>

                        <Field
                            label="Email"
                            name="email"
                            component={LabeledInputField}
                            type="text"
                            placeholder="e.g name@domain.com"
                        />

                        <Field
                            label="First Name"
                            name="firstName"
                            component={LabeledInputField}
                            type="text"
                            placeholder=""
                        />

                        <Field
                            label="Last Name"
                            name="lastName"
                            component={LabeledInputField}
                            type="text"
                            placeholder=""
                        />

                        <Spacer size="small" />

                        <div className="row">
                            <div className="columns small-11 small-centered">
                                <Button
                                    nativeType="submit"
                                    type="primary"
                                    className="expanded"
                                    disabled={
                                        pristine ||
                                        submitting ||
                                        impersonatedBy === 'salesperson'
                                    }
                                >
                                    Invite User
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </Form>
        </div>
    )
}

InviteUserForm.propTypes = {
    companyName: PropTypes.string,
    impersonatedBy: PropTypes.string,
    sapId: PropTypes.string,
}

const RoutedInviteUserForm = withRouter(InviteUserForm)

export default RoutedInviteUserForm
